import React, { useState, useEffect } from 'react';
import { AspectRatio, Box, Button, Text, Link, Stack } from '@chakra-ui/react';
import { LocaleContext } from './layout';

export const CONSENT_GRANTED_EVENT = 'external-media-consent-granted';
export const CONSENT_REVOKED_EVENT = 'external-media-consent-revoked';

// Session-level "load once" acceptances (cleared on page reload)
const sessionAccepted = new Set<string>();

const isGlobalConsentGiven = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (window as any).CC?.allowedCategory?.('external_media') === true;
};

const getServiceName = (src: string): string => {
  if (src.includes('youtube') || src.includes('youtu.be')) return 'YouTube';
  if (src.includes('vimeo')) return 'Vimeo';
  if (src.includes('google.com/maps') || src.includes('maps.google')) return 'Google Maps';
  if (src.includes('facebook')) return 'Facebook';
  if (src.includes('instagram')) return 'Instagram';
  if (src.includes('twitter') || src.includes('x.com')) return 'X (Twitter)';
  if (src.includes('tiktok')) return 'TikTok';
  try {
    return new URL(src).hostname.replace('www.', '');
  } catch {
    return 'external content';
  }
};

const getServiceTermsUrl = (src: string): string | null => {
  if (src.includes('youtube') || src.includes('youtu.be')) return 'https://www.youtube.com/t/terms';
  if (src.includes('vimeo')) return 'https://vimeo.com/terms';
  if (src.includes('google.com/maps') || src.includes('maps.google')) return 'https://policies.google.com/terms';
  return null;
};

const strings = {
  de: {
    notice: (service: string, termsUrl: string | null) => (
      <>
        Dieser Inhalt wird von <strong>{service}</strong> gehostet. Durch das Laden akzeptieren Sie
        {termsUrl ? (
          <> die{' '}
            <Link href={termsUrl} isExternal color="blue.300" fontSize="inherit">
              Nutzungsbedingungen
            </Link>{' '}
            von {service}.
          </>
        ) : (
          <> die Nutzungsbedingungen des Drittanbieters.</>
        )}
      </>
    ),
    loadBtn: 'Inhalt laden',
    loadAllBtn: 'Immer erlauben',
  },
  en: {
    notice: (service: string, termsUrl: string | null) => (
      <>
        This content is hosted by <strong>{service}</strong>. By loading it you accept
        {termsUrl ? (
          <>{' '}<Link href={termsUrl} isExternal color="blue.300" fontSize="inherit">
            {service}'s terms and conditions
          </Link>.</>
        ) : (
          <> the third-party's terms and conditions.</>
        )}
      </>
    ),
    loadBtn: 'Load content',
    loadAllBtn: 'Always allow',
  },
  fr: {
    notice: (service: string, termsUrl: string | null) => (
      <>
        Ce contenu est hébergé par <strong>{service}</strong>. En le chargeant, vous acceptez
        {termsUrl ? (
          <>{' '}les{' '}
            <Link href={termsUrl} isExternal color="blue.300" fontSize="inherit">
              conditions d'utilisation
            </Link>{' '}
            de {service}.
          </>
        ) : (
          <> les conditions d'utilisation du tiers.</>
        )}
      </>
    ),
    loadBtn: 'Charger le contenu',
    loadAllBtn: 'Toujours autoriser',
  },
};

type Props = {
  src: string;
  allow?: string;
  ratio?: number;
};

export const ExternalEmbed: React.FC<Props> = ({ src, allow, ratio = 16 / 9 }) => {
  const { locale } = React.useContext(LocaleContext);
  const langKey = locale.split('-')[0] as keyof typeof strings;
  const t = strings[langKey in strings ? langKey : 'de'];

  const service = getServiceName(src);
  const termsUrl = getServiceTermsUrl(src);

  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    setAccepted(isGlobalConsentGiven() || sessionAccepted.has(src));

    const grant = () => setAccepted(true);
    const revoke = () => {
      if (!sessionAccepted.has(src)) setAccepted(false);
    };
    window.addEventListener(CONSENT_GRANTED_EVENT, grant);
    window.addEventListener(CONSENT_REVOKED_EVENT, revoke);
    return () => {
      window.removeEventListener(CONSENT_GRANTED_EVENT, grant);
      window.removeEventListener(CONSENT_REVOKED_EVENT, revoke);
    };
  }, [src]);

  const loadOnce = () => {
    sessionAccepted.add(src);
    setAccepted(true);
  };

  const alwaysAllow = () => {
    sessionAccepted.add(src);
    setAccepted(true);
    // Also accept via CookieConsent if available
    if ((window as any).CC) {
      (window as any).CC.accept(['external_media']);
    }
  };

  return (
    <AspectRatio ratio={ratio} w="full" mb="2">
      {accepted ? (
        <iframe
          src={src}
          allow={allow ?? 'accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen'}
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ border: 'none', width: '100%', height: '100%' }}
        />
      ) : (
        <Box
          bg="#0b1016"
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="full"
          h="full"
        >
          <Stack spacing={4} align="center" maxW="32em" px={6} textAlign="center">
            <Text color="white" fontSize="sm" lineHeight="1.6">
              {t.notice(service, termsUrl)}
            </Text>
            <Stack direction="row" spacing={3} flexWrap="wrap" justify="center">
              <Button size="sm" colorScheme="blue" onClick={loadOnce}>
                {t.loadBtn}
              </Button>
              <Button
                size="sm"
                variant="outline"
                color="white"
                borderColor="whiteAlpha.600"
                _hover={{ bg: 'whiteAlpha.200' }}
                onClick={alwaysAllow}
              >
                {t.loadAllBtn}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
    </AspectRatio>
  );
};
